import { z } from "zod";
/**
 * Field and choice ids are generated in the builder with `newFieldId()` and are
 * NEVER reused. Deleting a field and adding it back yields a new id, which is
 * what lets responses stay readable after the form changes: a response keys its
 * answers by field id, never by position, and a renamed field keeps its id.
 */
export const fieldIdSchema = z.string().regex(/^[A-Za-z0-9_-]{8,24}$/);
export const choiceIdSchema = z.string().regex(/^[A-Za-z0-9_-]{4,24}$/);
export const choiceSchema = z.object({
    id: choiceIdSchema,
    /**
     * Deliberately no `.min(1)`, for the same reason `fields` has none: a draft
     * must stay saveable while it is being written. Adding an option in the
     * builder creates it blank and you type into it — if that intermediate
     * state were invalid, autosave would 400 and the work would sit unsaved.
     * `assertPublishable()` is what refuses to publish a blank option.
     */
    label: z.string().trim().max(300),
});
/** Groups a file_upload field accepts, mapped to concrete mime types in mime.ts. */
export const FILE_ACCEPT_GROUPS = [
    "image",
    "pdf",
    "doc",
    "sheet",
    "presentation",
    "archive",
    "any",
];
const inputBase = {
    id: fieldIdSchema,
    label: z.string().trim().min(1).max(300),
    description: z.string().trim().max(1000).optional(),
    required: z.boolean().default(false),
};
const layoutBase = {
    id: fieldIdSchema,
};
export const fieldSchema = z.discriminatedUnion("type", [
    // ── Layout blocks. Carry no answer, never appear in responses or CSV. ──
    z.object({
        ...layoutBase,
        type: z.literal("heading"),
        text: z.string().trim().min(1).max(300),
        level: z.union([z.literal(1), z.literal(2), z.literal(3)]).default(2),
    }),
    z.object({
        ...layoutBase,
        type: z.literal("paragraph"),
        text: z.string().trim().min(1).max(2000),
    }),
    // ── Input fields ──
    z.object({
        ...inputBase,
        type: z.literal("short_text"),
        placeholder: z.string().max(100).optional(),
        minLength: z.number().int().min(0).max(500).optional(),
        maxLength: z.number().int().min(1).max(500).default(500),
    }),
    z.object({
        ...inputBase,
        type: z.literal("long_text"),
        placeholder: z.string().max(100).optional(),
        minLength: z.number().int().min(0).max(5000).optional(),
        maxLength: z.number().int().min(1).max(5000).default(5000),
        rows: z.number().int().min(2).max(12).default(4),
    }),
    z.object({
        ...inputBase,
        type: z.literal("multiple_choice"),
        choices: z.array(choiceSchema).min(1).max(100),
        allowOther: z.boolean().default(false),
        randomize: z.boolean().default(false),
    }),
    z.object({
        ...inputBase,
        type: z.literal("checkboxes"),
        choices: z.array(choiceSchema).min(1).max(100),
        allowOther: z.boolean().default(false),
        randomize: z.boolean().default(false),
        minSelected: z.number().int().min(0).max(100).optional(),
        maxSelected: z.number().int().min(1).max(100).optional(),
    }),
    z.object({
        ...inputBase,
        type: z.literal("dropdown"),
        choices: z.array(choiceSchema).min(1).max(500),
        placeholder: z.string().max(100).optional(),
    }),
    z.object({
        ...inputBase,
        type: z.literal("email"),
        placeholder: z.string().max(100).optional(),
    }),
    z.object({
        ...inputBase,
        type: z.literal("number"),
        placeholder: z.string().max(100).optional(),
        min: z.number().optional(),
        max: z.number().optional(),
        decimals: z.number().int().min(0).max(6).default(0),
        prefix: z.string().max(8).optional(),
        suffix: z.string().max(8).optional(),
    }),
    z.object({
        ...inputBase,
        type: z.literal("date"),
        /**
         * Answers are stored as the raw ISO string ("2026-08-08" or
         * "2026-08-08T14:30"), not a timestamptz. A date-only answer has no
         * timezone, and coercing it to UTC midnight is the classic off-by-one-day
         * bug — someone in UTC-5 picks the 8th and the owner reads the 7th.
         */
        mode: z.enum(["date", "datetime"]).default("date"),
        min: z.string().optional(),
        max: z.string().optional(),
    }),
    z.object({
        ...inputBase,
        type: z.literal("rating"),
        shape: z.enum(["star", "heart", "number"]).default("star"),
        max: z.number().int().min(3).max(10).default(5),
    }),
    z.object({
        ...inputBase,
        type: z.literal("file_upload"),
        maxFiles: z.number().int().min(1).max(10).default(1),
        /** Capped at 20MB: Cloud Run's HTTP/1 request body limit is 32MB. */
        maxSizeMb: z.number().int().min(1).max(20).default(10),
        accept: z.array(z.enum(FILE_ACCEPT_GROUPS)).max(6).default([]),
    }),
]);
export const LAYOUT_FIELD_TYPES = ["heading", "paragraph"];
/** Layout blocks carry no answer, so they are absent from responses and CSV. */
export const isInputField = (field) => !LAYOUT_FIELD_TYPES.includes(field.type);
export const hasChoices = (field) => "choices" in field;
//# sourceMappingURL=fields.js.map